<template>
    <div class="space-y-5">
        <!-- ===== Focus mode: the lease builder takes over the entire tab ===== -->
        <template v-if="wizardFocusMode">
            <div class="flex items-center justify-between">
                <button
                    type="button"
                    class="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors"
                    @click="exitWizardFocus"
                >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
                    Back to lease overview
                </button>
                <span class="hidden sm:block text-xs text-gray-400 truncate max-w-xs">{{ propertyStreetLine }}</span>
            </div>

            <!-- Tenant roster step (precedes the wizard for a fresh lease) -->
            <div v-if="showRosterStep" class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <TenantRosterStep
                    :defaults="leaseRosterDefaults"
                    @submit="handleRosterSubmit"
                    @cancel="cancelRoster"
                />
            </div>

            <!-- Manage Signers (add/remove/edit signers on a generated-but-unsent lease) -->
            <div v-else-if="showManageSigners" class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <TenantRosterStep
                    :defaults="manageSignersDefaults"
                    heading="Manage signers"
                    submit-label="Save & Regenerate"
                    busy-label="Regenerating…"
                    :busy="savingSigners"
                    :server-error="manageSignersError"
                    @submit="handleManageSignersSubmit"
                    @cancel="showManageSigners = false"
                />
            </div>

            <!-- Cancel signature request (pull a lease back to correct it) -->
            <div v-else-if="showCancelSignature" class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <CancelSignatureStep
                    :signers="signers"
                    :busy="cancellingSignature"
                    :server-error="cancelSignatureError"
                    @submit="handleCancelSignature"
                    @cancel="showCancelSignature = false"
                />
            </div>

            <!-- Wizard panel -->
            <div v-else-if="showWizard" class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <RocketLawyerInterviewWizard
                    :key="wizardSessionKey"
                    :property-id="property.id"
                    :initial-interview="rocketLawyerInterview"
                    :restart="forceWizardRestart"
                    :tenants="rosterTenants"
                    @completed="handleWizardCompleted"
                    @saved="exitWizardFocus"
                />
            </div>

            <!-- Document wizard panel (eviction notices / general forms, RAC-10324) -->
            <div v-else-if="documentWizard" class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <RocketLawyerInterviewWizard
                    :key="wizardSessionKey"
                    :property-id="property.id"
                    :category="documentWizard.category"
                    :template-name="documentWizard.templateName"
                    :interview-id="documentWizard.interviewId"
                    :document-name="documentWizard.documentName"
                    @completed="handleDocumentWizardCompleted"
                    @saved="exitWizardFocus"
                    @upgrade-required="handleDocumentUpgradeRequired"
                />
            </div>

            <!-- Pull a document back from signature so it can be corrected (RAC-10324) -->
            <div v-else-if="documentCancelStep" class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <CancelSignatureStep
                    :signers="documentCancelStep.signers"
                    :document-name="documentCancelStep.documentName"
                    :busy="cancellingDocument"
                    :server-error="documentCancelError"
                    @submit="handleCancelDocumentSignature"
                    @cancel="documentCancelStep = null"
                />
            </div>

            <!-- Send a generated document for e-signature (RAC-10324) -->
            <div v-else-if="documentSignerStep" class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <DocumentSignerStep
                    :document-name="documentSignerStep.documentName"
                    :signers="documentSignerStep.signers"
                    :busy="sendingDocument"
                    :server-error="documentSendError"
                    @submit="handleSendDocument"
                    @cancel="documentSignerStep = null"
                />
            </div>

            <!-- Collect the roster for an uploaded lease (RAC-10257), then hand
                 off to RocketLawyer's editor to place the signature boxes. -->
            <div v-else-if="uploadedLeaseSignerStep" class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <UploadedLeaseSignerStep
                    :landlord-name="agentName"
                    :landlord-email="agentEmail"
                    :busy="preparingUploadedLease"
                    :server-error="uploadedLeaseError"
                    @submit="handlePrepareUploadedLease"
                    @cancel="uploadedLeaseSignerStep = null"
                />
            </div>
        </template>

        <!-- ===== Overview mode ===== -->
        <template v-else>
        <!-- RAC-10292: while RocketLawyer leases aren't live, the whole tab is
             shown but blurred and non-interactive behind a "Coming Soon" overlay —
             this is what stops lease creation from routing to LawDepot. -->
        <div class="relative">
        <div class="space-y-5" :class="{ 'pointer-events-none select-none blur-sm': leaseComingSoon }" :aria-hidden="leaseComingSoon ? 'true' : null">
        <!-- Create New Lease button -->
        <div v-if="!isSharedViewOnly" class="flex justify-end">
            <button
                type="button"
                class="inline-flex items-center gap-2 bg-rent-risk-blue-400 hover:bg-black text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
                @click="openCreateChooser"
            >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Draft a Document
            </button>
        </div>
        <p v-if="leaseFeeError" class="text-xs text-red-600 text-right">{{ leaseFeeError }}</p>

        <!-- How it works (dismissable, shared component) -->
        <HowItWorksBanner
            description="Follow these steps to create a legal, state-specific lease, signed electronically."
            :steps="howItWorksSteps"
            storage-key="lease_how_it_works_dismissed"
        />

        <!-- Marketing Banner — dismissable, remembered like the How it works banner
             above it (RAC-10476). Closing it also hides the Get E-Sign upsell. -->
        <div v-if="showMarketingBanner" class="relative bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <button
                type="button"
                class="absolute top-3 right-3 z-10 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                aria-label="Dismiss lease agreements banner"
                @click="dismissMarketingBanner"
            >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div class="flex flex-col md:flex-row">
                <!-- Illustration -->
                <div class="flex items-center justify-center md:w-48 shrink-0">
                    <img :src="leaseIllustration" alt="Lease agreement illustration" class="w-full h-auto" />
                </div>

                <!-- Text + Features -->
                <div class="flex-1 p-6 border-t md:border-t-0 md:border-l border-gray-100" :class="{ 'pr-12': isRiskGuardPro }">
                    <h2 class="text-lg font-bold text-gray-900">We make lease agreements simple</h2>
                    <p class="text-sm text-gray-500 mt-1">
                        Easily draft lease agreements or real estate contracts, sign documents electronically or consult
                        with real estate attorneys — all within minutes and directly from your dashboard.
                    </p>
                    <div class="flex flex-col sm:flex-row sm:justify-between gap-5 mt-4">
                        <div class="flex items-center gap-2">
                            <svg class="w-6 h-6 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                            <span class="text-xs text-gray-600 font-medium">State-specific<br>legal protection</span>
                        </div>
                        <div class="flex items-center gap-2">
                            <svg class="w-6 h-6 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                            <span class="text-xs text-gray-600 font-medium">E-sign in<br>minutes</span>
                        </div>
                        <div class="flex items-center gap-2">
                            <svg class="w-6 h-6 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                            <span class="text-xs text-gray-600 font-medium">Attorney support<br>when you need it</span>
                        </div>
                    </div>
                </div>

                <!-- Divider -->
                <div v-if="!isRiskGuardPro" class="hidden md:block w-px bg-gray-100 my-4"></div>

                <!-- Get E-Sign (RiskGuard Pro upsell) -->
                <div v-if="!isRiskGuardPro" class="p-6 md:w-72 shrink-0 border-t md:border-t-0 bg-gradient-to-br from-rent-risk-blue-100 to-white">
                    <p class="text-sm font-bold text-rent-risk-blue-400">Need E-Sign? Upgrade to Pro</p>
                    <p class="text-xs text-gray-500 mt-1.5 mb-4 leading-relaxed">Easily draft and sign lease documents in minutes with RiskGuard Pro.</p>
                    <button
                        type="button"
                        class="w-full text-sm font-semibold text-white bg-rent-risk-blue-400 hover:bg-black rounded-lg px-4 py-2.5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        :disabled="isUpgradingToProFromUpsell"
                        @click="openEsignUpsell"
                    >
                        Get E-Sign
                    </button>
                    <p class="text-xs text-gray-500 mt-3 flex items-center gap-1.5">
                        <svg class="w-3.5 h-3.5 text-green-500 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
                        {{ proStartingAtCopy }}
                    </p>
                </div>
            </div>
        </div>

        <!-- Body. The right-hand Activity rail is gone: each document's activity
             now lives in its ⋯ menu under "Your Documents" (RAC-10476). -->
        <div class="flex flex-col lg:flex-row gap-5">

            <!-- Current Lease + Wizard -->
            <div class="flex-1 min-w-0 space-y-4">

                <!-- No lease — RocketLawyer empty state. A manually-uploaded lease
                     surfaces in "Your Documents", not here, so this shows whenever
                     there's no in-flight RocketLawyer lease. -->
                <template v-if="rocketLawyerEnabled && !rocketLawyerInterview">
                    <div class="flex items-center justify-between">
                        <h2 class="flex items-center gap-2 text-base font-semibold text-gray-900"><span class="w-1 h-5 rounded-full bg-purple-600"></span>Current Lease</h2>
                    </div>
                    <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-10 flex flex-col items-center text-center gap-4">
                        <div class="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                            <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        </div>
                        <div>
                            <p class="text-sm font-semibold text-gray-800">No lease created yet</p>
                            <p class="text-xs text-gray-500 mt-1">Build a state-specific lease step-by-step.</p>
                        </div>
                        <div v-if="!isSharedViewOnly" class="flex gap-3 flex-wrap justify-center">
                            <button type="button" class="primaryButton" @click="openCreateChooser">Create a Lease</button>
                        </div>
                        <ToolTip v-else-if="viewOnlyTooltip" :title="viewOnlyTooltip.title" :description="viewOnlyTooltip.description" />
                    </div>
                </template>

                <!-- No lease — LawDepot empty state -->
                <template v-else-if="!rocketLawyerEnabled && !leaseDocument">
                    <div class="flex items-center justify-between">
                        <h2 class="flex items-center gap-2 text-base font-semibold text-gray-900"><span class="w-1 h-5 rounded-full bg-purple-600"></span>Current Lease</h2>
                    </div>
                    <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-10 flex flex-col items-center text-center gap-4">
                        <div class="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                            <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        </div>
                        <div>
                            <p class="text-sm font-semibold text-gray-800">No lease created yet</p>
                            <p class="text-xs text-gray-500 mt-1">Create a state-specific lease powered by LawDepot.</p>
                        </div>
                        <div class="flex gap-3 flex-wrap justify-center">
                            <button v-if="!isSharedViewOnly" type="button" class="primaryButton" @click="openCreateChooser">Create a Lease</button>
                            <ToolTip v-else-if="viewOnlyTooltip" :title="viewOnlyTooltip.title" :description="viewOnlyTooltip.description" />
                        </div>
                    </div>
                </template>

                <!-- Lease exists (RocketLawyer interview OR uploaded document) -->
                <template v-else>
                    <!-- Section header -->
                    <div class="flex items-center justify-between">
                        <h2 class="flex items-center gap-2 text-base font-semibold text-gray-900"><span class="w-1 h-5 rounded-full bg-purple-600"></span>Current Lease</h2>
                    </div>

                    <!-- In-progress lease card -->
                    <div
                        v-if="rocketLawyerEnabled && rocketLawyerInterview && rocketLawyerInterview.status === 'in_progress'"
                        class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
                    >
                        <div class="p-5">
                            <div class="flex items-start gap-4">
                                <div class="w-10 h-10 bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center shrink-0">
                                    <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                </div>
                                <div>
                                    <span class="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-rent-risk-blue-100 text-rent-risk-blue-400 mb-1">In Progress</span>
                                    <p class="text-base font-bold text-gray-900">{{ propertyStreetLine }}</p>
                                    <p class="text-sm text-gray-500">{{ property.city }}, {{ property.state }} {{ property.zip_code }}</p>
                                    <p v-if="resumeProgressLabel" class="text-xs text-gray-400 mt-1">{{ resumeProgressLabel }}</p>
                                </div>
                            </div>
                        </div>
                        <div v-if="downloadUrl || !isSharedViewOnly" class="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
                            <a
                                v-if="downloadUrl"
                                :href="downloadUrl"
                                target="_blank"
                                rel="noopener"
                                class="inline-flex items-center gap-2 text-sm font-medium text-gray-700 border border-gray-300 bg-white hover:bg-gray-50 rounded-lg px-3 py-1.5 transition-colors"
                            >
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                Preview Lease
                            </a>
                            <button v-if="!isSharedViewOnly" type="button" class="primaryButton" @click="openWizard()">Resume Lease</button>
                        </div>
                        <ToolTip v-else-if="viewOnlyTooltip" class="px-5 py-3" :title="viewOnlyTooltip.title" :description="viewOnlyTooltip.description" />
                    </div>

                    <!-- Completed RocketLawyer lease card -->
                    <div
                        v-else-if="rocketLawyerEnabled && rocketLawyerInterview && rocketLawyerInterview.status === 'completed'"
                        class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
                    >
                        <!-- Address + badge -->
                        <div class="p-5 flex items-start gap-4">
                            <div class="w-10 h-10 bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center shrink-0">
                                <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            </div>
                            <div>
                                <span class="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold mb-1" :class="signingBadge.class">{{ signingBadge.label }}</span>
                                <p class="text-base font-bold text-gray-900">{{ leasePropertyAddress }}</p>
                                <p class="text-sm text-gray-500">{{ leasePropertyCity }}</p>
                            </div>
                        </div>

                        <!-- Details grid -->
                        <div class="px-5 pb-4 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 border-t border-gray-100 pt-4">
                            <div>
                                <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Tenant</p>
                                <p class="text-sm font-semibold text-gray-900 mt-1">{{ leaseTenantName }}</p>
                                <p class="text-xs text-gray-500">{{ leaseTenantCount }}</p>
                            </div>
                            <div>
                                <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Lease Term</p>
                                <p class="text-sm font-semibold text-gray-900 mt-1">{{ leaseTermDisplay }}</p>
                                <p v-if="leaseDuration" class="text-xs text-gray-500">{{ leaseDuration }}</p>
                            </div>
                            <div>
                                <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Monthly Rent</p>
                                <p class="text-sm font-semibold text-gray-900 mt-1">{{ leaseMonthlyRent }}</p>
                                <p class="text-xs text-gray-500">Due on the 1st</p>
                            </div>
                            <div>
                                <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Last Updated</p>
                                <p class="text-sm font-semibold text-gray-900 mt-1">{{ leaseLastUpdatedDate }}</p>
                                <p class="text-xs text-gray-500">{{ leaseLastUpdatedTime }}</p>
                            </div>
                        </div>

                        <!-- Contextual status banner -->
                        <div v-if="isReadyToSend && wasPulledBack" class="mx-5 mt-1 mb-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                            <svg class="w-4 h-4 text-amber-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6M3 10l6-6" /></svg>
                            <p class="text-xs text-amber-800">You pulled this lease back from signature. Make your changes, then send it again — every signer gets a fresh signing link.</p>
                        </div>
                        <div v-else-if="isReadyToSend" class="mx-5 mt-1 mb-3 flex items-start gap-2 bg-rent-risk-blue-100 border border-rent-risk-blue-400/20 rounded-lg px-4 py-3">
                            <svg class="w-4 h-4 text-rent-risk-blue-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <p class="text-xs text-gray-600">Your lease is ready. Send it for e-signature and each signer gets an email with their secure signing link.</p>
                        </div>
                        <div v-else-if="isSigning" class="mx-5 mt-1 mb-3 bg-orange-50 border border-orange-200 rounded-lg px-4 py-3">
                            <div class="flex items-start gap-2">
                                <svg class="w-4 h-4 text-orange-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                <div class="min-w-0 flex-1">
                                    <p class="text-xs text-gray-600">Sent for e-signature. Signatures update when you load or refresh this page.</p>
                                    <div v-if="!isSharedViewOnly" class="mt-2 flex items-center gap-3 flex-wrap">
                                        <button type="button" class="closeButton text-xs px-3 py-1 disabled:opacity-50" :disabled="refreshing || refreshRetryIn > 0" @click="refreshSigningStatus">{{ refreshing ? 'Refreshing…' : (refreshRetryIn > 0 ? `Refresh (${refreshRetryIn}s)` : 'Refresh') }}</button>
                                        <span v-if="signingLastRefreshed" class="text-xs text-gray-400">Last refreshed {{ signingLastRefreshed }}</span>
                                    </div>
                                    <p v-if="refreshError" class="mt-1 text-xs text-orange-600">{{ refreshError }}</p>
                                </div>
                            </div>
                        </div>
                        <div v-else-if="isSigned" class="mx-5 mt-1 mb-3 flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                            <svg class="w-4 h-4 text-green-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <p class="text-xs text-gray-600">All parties have signed. The executed lease is ready to download.</p>
                        </div>

                        <!-- Signers on this lease — always available once the lease is generated (RAC-10199) -->
                        <div v-if="signers.length" class="mx-5 mb-5">
                            <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Signers</p>
                            <ul class="border border-gray-200 rounded-lg divide-y divide-gray-100">
                                <li v-for="signer in signers" :key="signer.id" class="flex items-center justify-between px-4 py-3 gap-3">
                                    <div class="min-w-0">
                                        <p class="text-sm font-semibold text-gray-900 truncate">{{ signer.name }}</p>
                                        <p class="text-xs text-gray-500 capitalize">{{ signer.role }}</p>
                                    </div>
                                    <div class="flex items-center gap-2 shrink-0">
                                        <a v-if="signer.role === 'landlord' && signer.status === 'invited'" :href="appUrl(`/properties/${property.id}/lease-wizard/sign-as-landlord`)" class="primaryButton text-xs px-3 py-1">Sign Now</a>
                                        <span class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold" :class="signerStatusMeta(signer.status).class">
                                            <svg v-if="signer.status === 'signed'" class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
                                            {{ signerStatusMeta(signer.status).label }}
                                        </span>
                                    </div>
                                </li>
                            </ul>
                            <p class="mt-2 text-xs text-gray-400">{{ signerRosterNote }}</p>
                        </div>

                        <p v-if="sendError" class="mx-5 mb-3 text-xs text-red-600">{{ sendError }}</p>

                        <!-- Footer actions. Starting over lives in the top "Create New Lease" button (RAC-10199). -->
                        <div v-if="!isSharedViewOnly" class="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-2">
                            <button v-if="isReadyToSend" type="button" class="closeButton" @click="editLease">Edit Lease</button>
                            <button v-if="isReadyToSend" type="button" class="closeButton" @click="openManageSigners">Manage Signers</button>
                            <a v-if="rocketLawyerInterview.download_url" :href="rocketLawyerInterview.download_url" :class="isReadyToSend ? 'closeButton' : 'primaryButton'">{{ isSigned ? 'Download Executed PDF' : 'Download Draft' }}</a>
                            <button v-if="isSigning && isRiskGuardPro" type="button" class="closeButton" @click="openCancelSignature">Cancel Signature Request</button>
                            <button v-if="isReadyToSend && isRiskGuardPro" type="button" class="primaryButton" :disabled="sending" @click="sendForSignature">{{ sending ? 'Sending…' : 'Send for Signature' }}</button>
                            <button v-else-if="isReadyToSend" type="button" class="primaryButton" @click="openEsignUpsell">Get E-Sign</button>
                        </div>
                        <ToolTip v-else-if="viewOnlyTooltip" class="px-5 py-3" :title="viewOnlyTooltip.title" :description="viewOnlyTooltip.description" />
                    </div>

                    <!-- Legacy single-document card — non-RocketLawyer (LawDepot) mode
                         only. In RocketLawyer mode every lease, uploaded or generated,
                         lives in "Your Documents" below. -->
                    <div v-if="leaseDocument && !rocketLawyerEnabled" class="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-3">
                                <div class="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center shrink-0">
                                    <svg class="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                </div>
                                <div>
                                    <a :href="leaseDocument.url" target="_blank" class="text-sm font-medium text-purple-700 hover:text-purple-900 underline">{{ leaseDocument.file_name }}</a>
                                    <p class="text-xs text-gray-500 mt-0.5">Uploaded {{ leaseDocument.uploaded_at }}</p>
                                </div>
                            </div>
                            <div v-if="!isSharedViewOnly" class="flex items-center gap-2">
                                <button type="button" :disabled="!!leaseSlotUploadRefusal" class="text-sm text-gray-600 hover:text-purple-700 font-medium px-3 py-1.5 border border-gray-300 rounded-md hover:border-purple-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-gray-600 disabled:hover:border-gray-300" @click="replaceLeaseDocument">Replace</button>
                                <button @click="confirmDelete" type="button" class="text-sm text-red-600 hover:text-red-800 font-medium px-3 py-1.5 border border-red-300 rounded-md hover:border-red-400 transition-colors">Delete</button>
                            </div>
                            <ToolTip v-else-if="viewOnlyTooltip" :title="viewOnlyTooltip.title" :description="viewOnlyTooltip.description" />
                        </div>
                        <!-- One lease per property (RAC-10257): Replace is the one
                             path the rule closes, and Delete beside it is exactly
                             what the message tells them to use. -->
                        <p v-if="leaseSlotUploadRefusal && !isSharedViewOnly" class="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-3">{{ leaseSlotUploadRefusal }}</p>
                    </div>
                </template>

            </div>
        </div>

        <!-- ===== Your Documents — this property's leases across their lifecycle (RAC-10264) ===== -->
        <div v-if="rocketLawyerEnabled && leaseDocuments.length" class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div class="px-5 pt-5 pb-3">
                <h2 class="flex items-center gap-2 text-base font-semibold text-gray-900"><span class="w-1 h-5 rounded-full bg-purple-600"></span>Your Documents</h2>
            </div>

            <!-- A document with no signature blocks, or a failed signers fetch —
                 surfaced inline rather than taking over the tab (RAC-10324). -->
            <p v-if="documentSignerNotice" class="mx-5 mb-3 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2">
                {{ documentSignerNotice }}
            </p>

            <!-- An upload that landed. The server says so and the row appears, but
                 the modal simply closing read as "did that work?" — so say it. -->
            <div v-if="uploadNotice" class="mx-5 mb-3 flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                <svg class="w-4 h-4 text-green-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <p class="text-xs text-gray-600 flex-1">{{ uploadNotice }}</p>
            </div>

            <!-- A send that was refused after the roster step closed (RAC-10257) —
                 e.g. the lease went out from another tab in the meantime. -->
            <p v-if="uploadedLeaseNotice" class="mx-5 mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                {{ uploadedLeaseNotice }}
            </p>

            <!-- Send found the lease already invited inside RocketLawyer's editor
                 and recorded it instead of sending it again (RAC-10257). Not an
                 error, but it must be said: RentRisk emailed nobody, so the
                 landlord is not waiting on a confirmation that never comes. -->
            <div v-if="uploadedLeaseAdoptedNotice" class="mx-5 mb-3 flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-lg px-4 py-3">
                <svg class="w-4 h-4 text-orange-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z" /></svg>
                <p class="text-xs text-gray-600 flex-1">{{ uploadedLeaseAdoptedNotice }}</p>
            </div>

            <!-- RAC-10361: outcome of a per-document Refresh. Documents advance on
                 the hourly reconcile or this button; the tab's on-load refresh
                 covers the lease only. -->
            <div v-if="documentRefreshMessage" class="mx-5 mb-3 flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-lg px-4 py-3">
                <svg class="w-4 h-4 text-orange-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <p class="text-xs text-gray-600 flex-1">{{ documentRefreshMessage }}</p>
            </div>

            <!-- Status filter pills -->
            <div class="px-5 pb-4 flex flex-wrap gap-2 border-b border-gray-100">
                <button
                    v-for="t in docFilterTabs"
                    :key="t.key"
                    type="button"
                    class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors"
                    :class="docFilter === t.key ? 'bg-rent-risk-blue-400 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
                    @click="docFilter = t.key"
                >
                    {{ t.label }}<span v-if="t.count" class="text-xs" :class="docFilter === t.key ? 'text-white/70' : 'text-gray-400'">{{ t.count }}</span>
                </button>
            </div>

            <!-- Table -->
            <div class="overflow-x-auto">
                <table class="w-full text-sm">
                    <thead>
                        <tr class="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                            <th class="px-5 py-3 font-semibold">Document Type</th>
                            <th class="px-5 py-3 font-semibold">Tenants</th>
                            <th class="px-5 py-3 font-semibold">Lease Term</th>
                            <th class="px-5 py-3 font-semibold">Status</th>
                            <th class="px-5 py-3 font-semibold">Last Updated</th>
                            <th class="px-5 py-3 font-semibold text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                        <tr v-for="row in filteredDocuments" :key="row.id" class="hover:bg-gray-50">
                            <td class="px-5 py-4">
                                <div class="flex items-center gap-3">
                                    <div class="w-9 h-9 bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center shrink-0">
                                        <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                    </div>
                                    <span class="font-semibold text-gray-900 whitespace-nowrap">{{ row.document_type }}</span>
                                </div>
                            </td>
                            <td class="px-5 py-4">
                                <!-- A row with no parties shows a plain hyphen instead of an empty
                                     "?? / 0 signers" stack (Rich on RAC-10324). Most generated
                                     documents are notices nobody signs, and an avatar placeholder
                                     reads as missing data rather than "not applicable". -->
                                <div v-if="row.tenant_count > 0" class="flex items-center gap-2">
                                    <!-- Every party, not just the first. A lease row has one tenant
                                         name worth showing, but a document's blocks are a set — and
                                         its first party is often the landlord, so rendering
                                         tenants[0] alone read as "only the landlord signed". -->
                                    <div class="flex -space-x-2 shrink-0">
                                        <span
                                            v-for="(party, partyIdx) in row.tenants.slice(0, 3)"
                                            :key="partyIdx"
                                            :title="party.name"
                                            class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-xs font-semibold text-gray-600 ring-2 ring-white"
                                        >{{ party.initials || '??' }}</span>
                                        <span
                                            v-if="row.tenant_count > 3"
                                            class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-xs font-semibold text-gray-500 ring-2 ring-white"
                                        >+{{ row.tenant_count - 3 }}</span>
                                    </div>
                                    <div class="min-w-0">
                                        <p class="text-gray-900 truncate" :title="partyNames(row)">{{ partyNames(row) || '—' }}</p>
                                        <p class="text-xs text-gray-500">{{ row.tenant_count }} {{ partyNoun(row) }}{{ row.tenant_count === 1 ? '' : 's' }}</p>
                                    </div>
                                </div>
                                <span v-else class="text-gray-700">—</span>
                            </td>
                            <td class="px-5 py-4 text-gray-700 whitespace-nowrap">{{ row.lease_term || '—' }}</td>
                            <td class="px-5 py-4">
                                <span class="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap" :class="docStatusPill(row.status)">{{ row.status_label }}</span>
                                <!-- Documents only. A lease row is built by snapshotRow() from its
                                     PropertyLeaseTerm, which carries no signed_count — so on a lease
                                     this rendered a permanent "0 of N signed" however many tenants
                                     had actually signed. -->
                                <p
                                    v-if="(row.category !== 'lease_agreement' || row.not_tracked) && row.status === 'pending_signature' && signingProgress(row)"
                                    class="text-xs text-gray-500 mt-1 whitespace-nowrap"
                                >
                                    {{ signingProgress(row) }}
                                </p>
                                <!-- RAC-10257: an uploaded lease is a stored document, never the
                                     property's active lease — no rent tracking, no renewal
                                     reminders, no lease history. Said plainly so a landlord who
                                     just watched it get signed doesn't assume otherwise. -->
                                <p
                                    v-if="row.not_tracked && row.status === 'executed'"
                                    class="text-xs text-gray-500 mt-1"
                                >
                                    Signed document — not tracked for rent
                                </p>
                            </td>
                            <td class="px-5 py-4 text-gray-500 whitespace-nowrap">{{ row.last_updated }}</td>
                            <td class="px-5 py-4 text-right">
                                <div v-if="row.action.type === 'continue'" class="inline-flex items-center gap-2">
                                    <button
                                        v-if="!isSharedViewOnly"
                                        type="button"
                                        class="closeButton text-xs"
                                        @click="handleContinueRow(row)"
                                    >
                                        Continue
                                    </button>
                                    <RowActionsMenu
                                        v-if="rowHasActivity(row)"
                                        :label="`More actions for ${row.document_type}`"
                                    >
                                        <button
                                            type="button"
                                            role="menuitem"
                                            class="block w-full px-4 py-2 text-left text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition"
                                            @click="openRowActivity(row)"
                                        >
                                            View Activity
                                        </button>
                                    </RowActionsMenu>
                                    <span v-if="isSharedViewOnly && !rowHasActivity(row)" class="text-xs text-gray-400">—</span>
                                </div>
                                <div v-else-if="row.action.type === 'view'" class="inline-flex items-center gap-2">
                                    <a
                                        v-if="row.action.download_url"
                                        :href="row.action.download_url"
                                        target="_blank"
                                        rel="noopener"
                                        class="closeButton text-xs"
                                    >
                                        View
                                    </a>
                                    <button
                                        v-if="row.action.can_send && isRiskGuardPro && !isSharedViewOnly"
                                        type="button"
                                        class="primaryButton text-xs px-3 py-1.5"
                                        :disabled="loadingSignersFor !== null"
                                        @click="openDocumentSignerStep(row)"
                                    >
                                        {{ loadingSignersFor === row.action.interview_id ? 'Loading…' : 'Send for Signature' }}
                                    </button>
                                    <button
                                        v-else-if="row.action.can_send && !isSharedViewOnly"
                                        type="button"
                                        class="primaryButton text-xs px-3 py-1.5"
                                        @click="openEsignUpsell"
                                    >
                                        Get E-Sign
                                    </button>
                                    <!-- Secondary actions live behind a ⋯ menu: with View, Send,
                                         Edit and Cancel all inline the cell wraps on anything
                                         narrower than a desktop. View Activity is read-only, so
                                         the menu now renders for view-only collaborators too
                                         and each write action guards itself (RAC-10476). -->
                                    <RowActionsMenu
                                        v-if="rowMenuActions(row).length || rowHasActivity(row)"
                                        :label="`More actions for ${row.document_type}`"
                                    >
                                        <button
                                            v-if="rowHasActivity(row)"
                                            type="button"
                                            role="menuitem"
                                            class="block w-full px-4 py-2 text-left text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition"
                                            @click="openRowActivity(row)"
                                        >
                                            View Activity
                                        </button>
                                        <button
                                            v-if="row.action.can_edit && !isSharedViewOnly"
                                            type="button"
                                            role="menuitem"
                                            class="block w-full px-4 py-2 text-left text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition"
                                            @click="handleEditDocument(row)"
                                        >
                                            Edit Document
                                        </button>
                                        <button
                                            v-if="row.action.can_cancel && isRiskGuardPro && !isSharedViewOnly"
                                            type="button"
                                            role="menuitem"
                                            class="block w-full px-4 py-2 text-left text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition"
                                            @click="openCancelDocumentSignature(row)"
                                        >
                                            Cancel Signature Request
                                        </button>
                                        <button
                                            v-if="row.action.can_refresh && !isSharedViewOnly"
                                            type="button"
                                            role="menuitem"
                                            class="block w-full px-4 py-2 text-left text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition disabled:opacity-50"
                                            :disabled="refreshingDocumentId === row.action.interview_id || (documentRefreshRetryIn[row.action.interview_id] || 0) > 0"
                                            @click="refreshDocumentStatus(row)"
                                        >
                                            {{ refreshingDocumentId === row.action.interview_id ? 'Refreshing…' : 'Refresh Status' }}
                                        </button>
                                    </RowActionsMenu>
                                    <span v-if="!row.action.download_url && !row.action.can_send && !rowMenuActions(row).length && !rowHasActivity(row)" class="text-xs text-gray-400">—</span>
                                </div>
                                <div v-else-if="row.action.type === 'uploaded'" class="inline-flex items-center gap-2">
                                    <a
                                        :href="row.action.view_url"
                                        target="_blank"
                                        rel="noopener"
                                        class="closeButton text-xs"
                                    >
                                        View
                                    </a>
                                    <!-- RAC-10257: an uploaded lease can now go out for
                                         e-signature. Once it is set up, BOTH steps stay on the
                                         row — placing boxes happens at RocketLawyer and can be
                                         redone any number of times, so there is no moment where
                                         one replaces the other. Deliberately no Edit: we never
                                         edit an uploaded PDF, they replace it. -->
                                    <button
                                        v-if="row.action.can_place && isRiskGuardPro && !isSharedViewOnly"
                                        type="button"
                                        class="closeButton text-xs"
                                        @click="goToPlacement(row)"
                                    >
                                        Place Signature Boxes
                                    </button>
                                    <!-- Every tenant has signed and the landlord's countersignature
                                         is all that is left. The generated lease has offered this
                                         since RAC-10174, from its signer roster; an uploaded lease
                                         has no roster, so it goes on the row. Deliberately not
                                         Pro-gated — the lease is already out for signature, and
                                         blocking the last signature would strand it. -->
                                    <a
                                        v-if="row.action.can_countersign && !isSharedViewOnly"
                                        :href="row.action.countersign_url"
                                        class="primaryButton text-xs px-3 py-1.5"
                                    >
                                        Sign Now
                                    </a>
                                    <button
                                        v-if="uploadedPrimaryAction(row) && isRiskGuardPro && !isSharedViewOnly"
                                        type="button"
                                        class="primaryButton text-xs px-3 py-1.5"
                                        :disabled="preparingUploadedLease"
                                        @click="handleUploadedPrimaryAction(row)"
                                    >
                                        {{ uploadedActionFor === row.action.interview_id ? 'Sending…' : uploadedPrimaryAction(row).label }}
                                    </button>
                                    <button
                                        v-else-if="uploadedPrimaryAction(row) && !isSharedViewOnly"
                                        type="button"
                                        class="primaryButton text-xs px-3 py-1.5"
                                        @click="openEsignUpsell"
                                    >
                                        Get E-Sign
                                    </button>
                                    <RowActionsMenu
                                        v-if="uploadedRowMenuActions(row).length || rowHasActivity(row)"
                                        :label="`More actions for ${row.document_type}`"
                                    >
                                        <button
                                            v-if="rowHasActivity(row)"
                                            type="button"
                                            role="menuitem"
                                            class="block w-full px-4 py-2 text-left text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition"
                                            @click="openRowActivity(row)"
                                        >
                                            View Activity
                                        </button>
                                        <button
                                            v-if="row.action.can_cancel && isRiskGuardPro && !isSharedViewOnly"
                                            type="button"
                                            role="menuitem"
                                            class="block w-full px-4 py-2 text-left text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition"
                                            @click="openCancelUploadedLeaseSignature(row)"
                                        >
                                            Cancel Signature Request
                                        </button>
                                        <button
                                            v-if="row.action.can_refresh && !isSharedViewOnly"
                                            type="button"
                                            role="menuitem"
                                            class="block w-full px-4 py-2 text-left text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition disabled:opacity-50"
                                            :disabled="refreshingDocumentId === row.action.interview_id || (documentRefreshRetryIn[row.action.interview_id] || 0) > 0"
                                            @click="refreshUploadedLeaseStatus(row)"
                                        >
                                            {{ refreshingDocumentId === row.action.interview_id ? 'Refreshing…' : 'Refresh Status' }}
                                        </button>
                                        <!-- Delete moves into the menu only once this lease has a
                                             signing lifecycle; before that it stays inline, where
                                             landlords already expect it. Keyed on the write
                                             actions, not on the menu rendering — View Activity
                                             alone opens the menu and must not duplicate it. -->
                                        <button
                                            v-if="!isSharedViewOnly && uploadedRowMenuActions(row).length"
                                            type="button"
                                            role="menuitem"
                                            class="block w-full px-4 py-2 text-left text-sm leading-5 text-red-600 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition"
                                            @click="confirmDeleteUploadedDocument(row.action.document_id, row)"
                                        >
                                            Delete
                                        </button>
                                    </RowActionsMenu>
                                    <button
                                        v-if="!isSharedViewOnly && !uploadedRowMenuActions(row).length"
                                        type="button"
                                        class="text-xs text-red-600 hover:text-red-800 font-medium px-3 py-1.5 border border-red-300 rounded-md hover:border-red-400 transition-colors"
                                        @click="confirmDeleteUploadedDocument(row.action.document_id, row)"
                                    >
                                        Delete
                                    </button>
                                </div>
                                <span v-else class="text-xs text-gray-400">—</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="px-5 py-3 border-t border-gray-100 text-xs text-gray-500">
                Showing {{ filteredDocuments.length }} of {{ leaseDocuments.length }} document{{ leaseDocuments.length === 1 ? '' : 's' }}
            </div>
        </div>
        </div>
        <!-- Coming Soon overlay (RAC-10292): large message over the blurred,
             non-interactive tab; keyed on the same flag, so it disappears the
             moment RocketLawyer leases go live. -->
        <div v-if="leaseComingSoon" class="absolute inset-0 z-10 flex items-start justify-center px-4 pt-10 sm:pt-16">
            <div class="w-full max-w-lg rounded-2xl border border-gray-200 bg-white/95 px-8 py-10 text-center shadow-xl">
                <div class="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-rent-risk-blue-100">
                    <svg class="h-8 w-8 text-rent-risk-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <h2 class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Coming Soon</h2>
                <p class="mx-auto mt-3 max-w-md text-sm text-gray-500">
                    We're upgrading lease agreements with a fully integrated, state-specific lease builder and e-signature — right inside your dashboard. This will be available here shortly.
                </p>
            </div>
        </div>
        </div>
        </template>

        <!-- "Draft a Document" chooser (RAC-10324; formerly the RAC-10253 Create
             Lease chooser, extracted). -->
        <DraftDocumentModal
            :show="showCreateChooser"
            :catalog-count="documentCatalog.length || 30"
            :upload-refusal="leaseSlotUploadRefusal"
            :new-lease-refusal="leaseSlotNewLeaseRefusal"
            @close="showCreateChooser = false"
            @start-lease="chooseStartNewLease"
            @upload-lease="chooseUploadExisting"
            @eviction-notice="chooseEvictionNotice"
            @explore-documents="chooseExploreDocuments"
        />

        <!-- "Explore Documents" — the general-form catalog (RAC-10324) -->
        <ExploreDocumentsModal
            :show="showExploreDocuments"
            :forms="documentCatalog"
            @close="showExploreDocuments = false"
            @select="handleExploreSelect"
        />

        <!-- Upload Modal -->
        <!-- A document's activity, opened from its ⋯ menu (RAC-10476) -->
        <ModalJetstream :show="activityRow !== null" :max-width="'md'" @close="closeRowActivity">
            <div v-if="activityRow" class="bg-white rounded-lg p-6 m-2">
                <div class="mb-5 flex items-center justify-between border-b pb-3">
                    <h2 class="text-lg font-bold text-gray-800">{{ activityRow.document_type }} Activity</h2>
                    <button type="button" aria-label="Close activity" class="text-gray-500 hover:text-gray-700 focus:outline-none" @click="closeRowActivity">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                <ol class="max-h-96 overflow-y-auto pr-1">
                    <li v-for="(item, idx) in activityRow.activity" :key="item.id" class="flex gap-3" :class="{ 'pb-4': idx < activityRow.activity.length - 1 }">
                        <div class="flex flex-col items-center shrink-0">
                            <div class="w-2.5 h-2.5 rounded-full mt-1 shrink-0 bg-gray-900"></div>
                            <div v-if="idx < activityRow.activity.length - 1" class="flex-1 w-px bg-gray-200 mt-1"></div>
                        </div>
                        <div class="flex-1 min-w-0 pb-1">
                            <div class="flex flex-wrap gap-x-2">
                                <span class="text-xs text-gray-500">{{ item.date }}</span>
                                <span class="text-xs text-gray-400">{{ item.time }}</span>
                            </div>
                            <p class="text-sm font-bold text-gray-900 mt-0.5">{{ item.title }}</p>
                            <p class="text-xs text-gray-500 mt-0.5">{{ item.description }}</p>
                        </div>
                    </li>
                </ol>
            </div>
        </ModalJetstream>

        <ModalJetstream :show="showUploadModal" :max-width="'lg'">
            <div class="bg-white rounded-lg p-6 m-2">
                <div class="mb-5 flex items-center justify-between border-b pb-3">
                    <h2 class="text-xl font-bold text-gray-800">Upload Lease Agreement</h2>
                    <button @click="closeUploadModal" class="text-gray-500 hover:text-gray-700 focus:outline-none">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                <div class="space-y-4">
                    <p class="text-sm text-gray-600">Upload your lease agreement document. Accepted formats: PDF, JPG, PNG, DOC, DOCX (max 10MB).</p>
                    <input ref="fileInput" type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" @change="handleFileChange" class="block w-full text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm p-2">
                    <p v-if="uploadError" class="text-xs text-red-600 bg-red-50 p-2 rounded">{{ uploadError }}</p>
                    <div class="flex justify-end space-x-3 mt-4">
                        <button @click="closeUploadModal" type="button" class="closeButton">Cancel</button>
                        <button @click="uploadFile" :disabled="!selectedFile || uploading" class="primaryButton" :class="{ 'opacity-25': !selectedFile || uploading }">
                            {{ uploading ? 'Uploading...' : 'Upload' }}
                        </button>
                    </div>
                </div>
            </div>
        </ModalJetstream>

        <!-- Delete Confirmation Modal -->
        <ModalJetstream :show="showDeleteModal" :max-width="'md'">
            <div class="bg-white rounded-lg p-6 m-2">
                <h2 class="text-lg font-bold text-gray-800 mb-3">{{ deletingAnExecutedLease ? 'Delete Signed Lease Agreement' : 'Delete Lease Agreement' }}</h2>
                <template v-if="deletingAnExecutedLease">
                    <p class="text-sm text-gray-600 mb-3">This is the <span class="font-semibold">signed</span> copy of this lease, and RentRisk keeps no other. Deleting it removes the executed contract and the signing record behind it for good.</p>
                    <p class="text-sm text-gray-600 mb-5">Download a copy first if you want to keep one.</p>
                </template>
                <p v-else class="text-sm text-gray-600 mb-5">Are you sure you want to delete this lease agreement? This action cannot be undone.</p>
                <!-- RAC-10257: the delete endpoint now refuses a lease that is out
                     for signature. Without somewhere to render that message the
                     modal would just stop spinning and look broken. -->
                <p v-if="deleteError" class="text-sm text-red-600 bg-red-50 p-2 rounded mb-4">{{ deleteError }}</p>
                <div class="flex justify-end space-x-3">
                    <button @click="closeDeleteModal" type="button" class="closeButton">Cancel</button>
                    <button @click="deleteDocument" :disabled="deleting" class="inline-flex items-center px-4 py-2 bg-red-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-red-700 focus:outline-none transition" :class="{ 'opacity-25': deleting }">
                        {{ deleting ? 'Deleting...' : 'Delete' }}
                    </button>
                </div>
            </div>
        </ModalJetstream>

        <!-- Create New Lease confirmation — replaces the existing lease (RAC-10199) -->
        <ModalJetstream :show="showNewLeaseModal" :max-width="'lg'">
            <div class="bg-white rounded-lg p-6 m-2">
                <h2 class="text-lg font-bold text-gray-800 mb-3">Create a new lease?</h2>
                <p class="text-sm text-gray-600 mb-3">
                    A new lease starts over from scratch and replaces the lease currently on this property. {{ newLeaseConsequence }} This can't be undone.
                </p>
                <div v-if="!hasProtectAccess" class="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 mb-3">
                    <svg class="w-4 h-4 text-amber-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
                    <p class="text-xs text-amber-800">
                        <strong>Heads up:</strong> generating a new lease is a separate $49 — the fee for your current lease doesn't carry over. RiskGuard Pro members get unlimited leases.
                    </p>
                </div>
                <div class="flex items-start gap-2 bg-rent-risk-blue-100 border border-rent-risk-blue-400/20 rounded-lg px-4 py-3 mb-5">
                    <svg class="w-4 h-4 text-rent-risk-blue-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <p class="text-xs text-gray-600">
                        Only need to change the rent, dates, or other terms? You don't have to start over — edit the existing lease instead.
                        Start fresh when you need <strong>different tenants</strong>, a <strong>different property or template</strong>, or a clean slate.
                    </p>
                </div>
                <div class="flex justify-end space-x-3">
                    <button @click="showNewLeaseModal = false" type="button" class="closeButton">Keep current lease</button>
                    <button @click="confirmNewLease" type="button" class="inline-flex items-center px-4 py-2 bg-red-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-red-700 focus:outline-none transition">
                        Create New Lease
                    </button>
                </div>
            </div>
        </ModalJetstream>

        <!-- RAC-10252: in-place RiskGuard Pro upsell for the "Get E-Sign" CTA -->
        <RiskGuardProUpsellModal
            :show="showEsignUpsellModal"
            :processing="isUpgradingToProFromUpsell"
            :stripe-data="canCollectCardInPlace ? stripeData : null"
            :notice="tierChangedNotice"
            @dismiss="handleEsignUpsellDecline"
            @upgrade="handleEsignUpsellUpgrade"
        />

        <!-- RAC-10228: wizard-entry gate — upgrade to Pro or pay $49/lease. 86bcd9hg3: also the
             gate for a property the landlord's plan doesn't cover (Pro is account-wide now, so
             there is no per-property upgrade to offer instead). -->
        <RiskGuardProUpsellModal
            :show="showLeaseGateModal"
            :processing="isUpgradingToProFromUpsell"
            :stripe-data="canCollectCardInPlace ? stripeData : null"
            :notice="tierChangedNotice"
            :decline-label="`No thanks, I'm fine paying ${LEASE_FEE_LABEL} for now`"
            @dismiss="dismissLeaseGate"
            @upgrade="handleLeaseGateUpgrade"
            @decline="handleLeaseGateDecline"
        />

        <!-- RAC-10261: single-step $49 lease purchase — collect card + charge here,
             no page reload. Rendered whenever a SetupIntent is available; :show toggles it. -->
        <AddCardPopup
            v-if="canCollectCardInPlace"
            :show="showLeaseCardModal"
            :stripe_data="stripeData"
            heading="Generate Your Lease"
            :notice-text="`One-time ${LEASE_FEE_LABEL} charge to generate this lease agreement. RiskGuard Pro members get unlimited leases plus e-sign.`"
            :confirm-button-text="`Pay ${LEASE_FEE_LABEL}`"
            @card-added="handleLeaseCardAdded"
            @close="cancelLeaseFeePayment"
        />

        <!-- RAC-10261: confirm the $49 charge when a card is already on file -->
        <ModalJetstream :show="showLeasePayConfirm" max-width="md">
            <div class="p-6 text-center">
                <h3 class="text-lg font-bold text-gray-900">Pay {{ LEASE_FEE_LABEL }} for this lease?</h3>
                <p class="mt-2 text-sm text-gray-600">
                    You'll be charged a one-time {{ LEASE_FEE_LABEL }} to generate this lease agreement.
                    RiskGuard Pro members get unlimited leases plus e-sign.
                </p>
                <p v-if="leaseFeeError" class="mt-3 text-xs text-red-600">{{ leaseFeeError }}</p>
                <div class="mt-6 flex justify-center gap-3">
                    <button type="button" class="closeButton" :disabled="payingLeaseFee" @click="cancelLeaseFeePayment">Cancel</button>
                    <button type="button" class="primaryButton" :disabled="payingLeaseFee" @click="confirmLeasePay">{{ payingLeaseFee ? 'Processing…' : `Pay ${LEASE_FEE_LABEL}` }}</button>
                </div>
            </div>
        </ModalJetstream>
    </div>
</template>

<script setup>
import { computed, ref, onUnmounted } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import ModalJetstream from '@/Jetstream/Modal.vue'
import axios from 'axios'
import ToolTip from '@/Shared/ToolTip.vue'
import RocketLawyerInterviewWizard from '@/Pages/V2/Properties/Tabs/RocketLawyerInterviewWizard.vue'
import TenantRosterStep from '@/Pages/V2/Properties/Tabs/TenantRosterStep.vue'
import CancelSignatureStep from '@/Pages/V2/Properties/Tabs/CancelSignatureStep.vue'
import DocumentSignerStep from '@/Pages/V2/Properties/Tabs/DocumentSignerStep.vue'
import UploadedLeaseSignerStep from '@/Pages/V2/Properties/Tabs/UploadedLeaseSignerStep.vue'
import HowItWorksBanner from '@/Pages/V2/Properties/Components/HowItWorksBanner.vue'
import RiskGuardProUpsellModal from '@/Components/RiskGuardProUpsellModal.vue'
import DraftDocumentModal from '@/Pages/V2/Properties/DraftDocumentModal.vue'
import RowActionsMenu from '@/Pages/V2/Properties/Components/RowActionsMenu.vue'
import { signingProgressLabel } from '@/Pages/V2/Properties/Tabs/signingProgressLabel'
import ExploreDocumentsModal from '@/Pages/V2/Properties/ExploreDocumentsModal.vue'
import AddCardPopup from '@/Shared/AddCardPopup.vue'
import { formatMonthly } from '@/Shared/proPlanState'
import { faFilePen, faMagnifyingGlass, faFileSignature, faGavel } from '@fortawesome/pro-solid-svg-icons'
import leaseIllustration from '../../../../../images/lease-agreement-illustration.jpg'

const props = defineProps({
    property: { type: Object, required: true },
    tab: { type: String, default: 'lease-agreement' },
    tabData: { type: Object, default: () => ({}) },
    propertyAccess: { type: String, default: 'owner' },
    viewOnlyTooltip: { type: Object, default: null },
    subscriptionType: { type: String, default: null },
    hasProtectAccess: { type: Boolean, default: false },
    isScreeningOnly: { type: Boolean, default: false },
    isAgentPaymentMethodAdded: { type: Boolean, default: false },
    stripeData: { type: [Object, Array], default: null },
    subscriptionProtectionRef: { type: Object, default: null },
})

const emit = defineEmits(['request-add-card', 'request-upgrade'])

const isSharedViewOnly = computed(() => props.propertyAccess === 'shared_view')
const leaseDocument = computed(() => props.tabData?.lease_document || null)
const lawdepotUrl = computed(() => props.tabData?.lawdepot_url || '')
const rocketLawyerEnabled = computed(() => !!props.tabData?.rocket_lawyer_enabled)
// RAC-10292: RocketLawyer leases aren't live yet. Until they are, the Contracts
// tab is shown but every action is gated behind a "Coming Soon" overlay so no
// lease creation can route out to LawDepot. Self-clears when the flag flips true.
const leaseComingSoon = computed(() => !rocketLawyerEnabled.value)
const rocketLawyerInterview = computed(() => props.tabData?.rocket_lawyer_interview || null)
const leaseRosterDefaults = computed(() => props.tabData?.lease_roster_defaults || [])
// RAC-10336: true when an unconsumed paid $49 lease credit already covers the next lease
// for this property, so the Create-Lease gate is skipped (see chooseStartNewLease).
const hasPrepaidLeaseCredit = computed(() => props.tabData?.has_prepaid_lease_credit === true)

// RAC-10324: non-lease documents (eviction notices + the general-form catalog)
// behind the "Draft a Document" modal. Included with Pro, no per-document fee —
// free-plan agents get the Pro upsell instead (per Rich on RAC-10324).
const documentCatalog = computed(() => props.tabData?.document_catalog || [])

// RAC-10336: leases follow the per-property tier model. A Managed property is covered by the
// subscription (unlimited leases); a Screening-Only property is pay-per-lease ($49) or an
// upgrade to Managed — even for RiskGuard/Pro, since the plan covers Managed properties only.
// Mirrors the backend SubscriptionAccess::leaseCoveredBySubscription() gate.
const leaseCoveredBySubscription = computed(() => !props.isScreeningOnly && props.hasProtectAccess)

// RAC-10257: e-signature is gated per PROPERTY as well as per account. Pro alone
// is not enough — a Screening-Only property is not covered by the plan, so it
// takes the same upgrade-or-pay-$49 gate a generated lease takes at creation.
// Mirrors the backend check in UploadedLeaseSigningController::prepare().
const esignCoveredForProperty = computed(
    () => leaseCoveredBySubscription.value || hasPrepaidLeaseCredit.value
)

// "Your Documents" list — this property's leases across their lifecycle
// (RAC-10264): draft interview + completed/superseded snapshots.
const leaseDocuments = computed(() => props.tabData?.lease_documents || [])
const docFilter = ref('all')
// Category pills (Eviction Notices / Forms, RAC-10324) render only when that
// category has rows; the status pills keep their original lease semantics.
const CATEGORY_PILLS = [
    { key: 'eviction_notice', label: 'Eviction Notices' },
    { key: 'form', label: 'Forms' },
]
const docFilterTabs = computed(() => {
    const counts = { all: leaseDocuments.value.length, draft: 0, pending_signature: 0, active: 0, expired: 0, uploaded: 0 }
    const categoryCounts = { eviction_notice: 0, form: 0 }
    leaseDocuments.value.forEach((d) => {
        if (counts[d.status] !== undefined) counts[d.status]++
        if (categoryCounts[d.category] !== undefined) categoryCounts[d.category]++
    })
    return [
        { key: 'all', label: 'All', count: counts.all },
        { key: 'draft', label: 'Draft', count: counts.draft },
        { key: 'pending_signature', label: 'Pending Signature', count: counts.pending_signature },
        { key: 'active', label: 'Active', count: counts.active },
        { key: 'expired', label: 'Expired', count: counts.expired },
        { key: 'uploaded', label: 'Uploaded', count: counts.uploaded },
        ...CATEGORY_PILLS
            .filter((pill) => categoryCounts[pill.key] > 0)
            .map((pill) => ({ ...pill, count: categoryCounts[pill.key], byCategory: true })),
    ]
})
const filteredDocuments = computed(() => {
    if (docFilter.value === 'all') {
        return leaseDocuments.value
    }
    const tab = docFilterTabs.value.find((t) => t.key === docFilter.value)
    return tab?.byCategory
        ? leaseDocuments.value.filter((d) => d.category === docFilter.value)
        : leaseDocuments.value.filter((d) => d.status === docFilter.value)
})
// A lease row lists tenants; a document row lists whatever its template calls
// its signature blocks, which are rarely tenants (RAC-10324).
const partyNoun = (row) => (row.category === 'lease_agreement' ? 'tenant' : 'signer')
const partyNames = (row) => (row.tenants || []).map((p) => p.name).filter(Boolean).join(', ')

// "N of M signed" while tenants are still signing, then what is actually being
// waited on. See signingProgressLabel.js for why the counter excludes the
// landlord and why that needs saying out loud once the tenants are done.
const signingProgress = (row) => signingProgressLabel({
    notTracked: row.not_tracked === true,
    tenantCount: row.tenant_count,
    signedCount: row.signed_count,
})

const docStatusPill = (status) => ({
    draft: 'bg-gray-100 text-gray-600',
    pending_signature: 'bg-orange-50 text-orange-600',
    active: 'bg-green-50 text-green-700',
    expired: 'bg-red-50 text-red-600',
    uploaded: 'bg-rent-risk-blue-50 text-rent-risk-blue-400',
    generated: 'bg-emerald-50 text-emerald-700',
    executed: 'bg-green-50 text-green-700',
}[status] || 'bg-gray-100 text-gray-600')

// RAC-10252: the lease "Get E-Sign" CTA upsells to RiskGuard Pro in place,
// reusing the same RiskGuardProUpsellModal + SubscriptionProtection plumbing
// the Tenant Screening tab uses (handleRiskGuardProUpsellUpgrade). E-Sign is a
// Pro feature, so the CTA only shows when the agent isn't already on Pro.
const isRiskGuardPro = computed(() => props.subscriptionType === 'rentrisk-protect-pro')
const showEsignUpsellModal = ref(false)
const isUpgradingToProFromUpsell = ref(false)
// Shared upgrade popup, opened by both the "Get E-Sign" CTA and the document
// wizard gate. Both upsell the same Pro plan via handleEsignUpsellUpgrade, so the
// checkout stays a single source of truth. ("Book a Call" was the third caller
// until RAC-10257 moved that CTA to the global sidebar.)

const openEsignUpsell = () => {
    if (isSharedViewOnly.value) { return }
    showEsignUpsellModal.value = true
}

// Which of this tab's two Pro pop-ups started the purchase, so a price change can
// reopen the same one.
const lastProUpsell = ref(null)
// Why a pop-up reopened (the Pro price moved before the charge); shown inside it.
const tierChangedNotice = ref(null)

const handleEsignUpsellUpgrade = () => {
    isUpgradingToProFromUpsell.value = true
    lastProUpsell.value = 'esign'
    tierChangedNotice.value = null
    storePendingProPurchase()
    showEsignUpsellModal.value = false
    if (props.subscriptionProtectionRef) {
        props.subscriptionProtectionRef.initiateSubscription(null)
    }
    isUpgradingToProFromUpsell.value = false
}

const handleEsignUpsellDecline = () => {
    tierChangedNotice.value = null
    showEsignUpsellModal.value = false
}

// Wizard-entry gate (RAC-10228). Free-plan agents must either upgrade to a plan
// (unlimited leases) or pay the one-time $49/lease fee before generating a new
// RocketLawyer lease. RiskGuard/Pro skip the gate entirely. The $49 itself is
// charged server-side when the wizard creates the interview — this modal only
// captures consent and ensures a card is on file first.
const LEASE_FEE_LABEL = '$49'
const showLeaseGateModal = ref(false)

// Park what the landlord was doing, show the upgrade-or-pay modal, and pick the
// action back up once they have paid. Without this the $49 route charges the
// card and drops them back on the list with nothing having happened.
const pendingGatedAction = ref(null)

const openLeaseGateFor = (resume) => {
    pendingGatedAction.value = resume
    showLeaseGateModal.value = true
}

const dismissLeaseGate = () => {
    tierChangedNotice.value = null
    pendingGatedAction.value = null
    showLeaseGateModal.value = false
}
const showLeaseCardModal = ref(false)
const showLeasePayConfirm = ref(false)
const leaseFeeError = ref(null)
const payingLeaseFee = ref(false)

// We can drive the in-place card form only when stripeData is the valid object
// shape (it can arrive as an empty array): both the publishable key (for
// Stripe()) and a SetupIntent must be present, matching Show.vue's validation.
const canCollectCardInPlace = computed(() =>
    !!props.stripeData
    && !Array.isArray(props.stripeData)
    && !!props.stripeData.publishable_key
    && !!props.stripeData.setup_intent
)

const handleLeaseGateUpgrade = () => {
    isUpgradingToProFromUpsell.value = true
    lastProUpsell.value = 'leaseGate'
    tierChangedNotice.value = null
    storePendingProPurchase()
    // pendingGatedAction stays parked: if the price changes before the charge, the gate
    // reopens and paying $49 should still resume the step.
    showLeaseGateModal.value = false
    if (props.subscriptionProtectionRef) {
        props.subscriptionProtectionRef.initiateSubscription(null)
    }
    isUpgradingToProFromUpsell.value = false
}

// 86bcd9hg3: Pro is one yearly plan for the account, charged at the tier the pop-up showed.
// expectedTierSlug lets the server refuse (409) if a property was added since.
const storePendingProPurchase = () => {
    sessionStorage.setItem('pending_subscription_plan', JSON.stringify({
        planSlug: 'protect-pro-tiered',
        billingCycle: 'annual',
        expectedTierSlug: page.props.auth?.user?.pro_plan?.quote_tier?.slug ?? null,
    }))
}

// Kick off the one-time $49/lease payment: collect a card in place when none is on
// file, otherwise confirm the charge.
const startLeaseFeePayment = () => {
    leaseFeeError.value = null
    if (! props.isAgentPaymentMethodAdded) {
        // No card on file: collect it AND charge in one step, in place — no page
        // reload. Fall back to the parent's add-card flow only if we have no
        // SetupIntent to drive the in-place form.
        if (canCollectCardInPlace.value) {
            showLeaseCardModal.value = true
        } else {
            emit('request-add-card')
        }
        return
    }
    // Card on file: confirm the one-time $49 before charging — no silent charge.
    showLeasePayConfirm.value = true
}

// Backing out of the $49 payment drops whatever step was waiting on it, so a later
// payment from somewhere else doesn't resume this one.
const cancelLeaseFeePayment = () => {
    showLeaseCardModal.value = false
    showLeasePayConfirm.value = false
    pendingGatedAction.value = null
}

const handleLeaseGateDecline = () => {
    tierChangedNotice.value = null
    showLeaseGateModal.value = false
    startLeaseFeePayment()
}

// Card just added in place (no reload) → charge the fee and continue.
const handleLeaseCardAdded = async ({ success, error }) => {
    showLeaseCardModal.value = false
    if (! success) {
        leaseFeeError.value = error || 'We could not save your card. Please try again.'
        return
    }
    await chargeAndStartLease()
}

const confirmLeasePay = async () => {
    showLeasePayConfirm.value = false
    await chargeAndStartLease()
}

// Charge the one-time $49/lease fee, then hand off to createNewLease — which
// shows the "replace current lease?" confirm when a lease already exists, and
// otherwise opens the wizard. The charge is idempotent server-side, so an
// interrupted attempt (or backing out of the replace confirm) won't double-bill
// — the paid credit is honored on the next lease creation.
const chargeAndStartLease = async () => {
    payingLeaseFee.value = true
    leaseFeeError.value = null
    try {
        await axios.post(`/properties/${props.property.id}/lease-wizard/pay`)

        const resume = pendingGatedAction.value
        pendingGatedAction.value = null

        if (resume) {
            resume()
        } else {
            createNewLease()
        }
    } catch (error) {
        leaseFeeError.value = error.response?.data?.message
            || 'We could not process the lease fee. Please try again.'
    } finally {
        payingLeaseFee.value = false
    }
}

// Signing state machine (RAC-10172). A completed interview moves
// null/in_preparation → sign_in_progress/partially_signed → signed.
const signingStatus = computed(() => rocketLawyerInterview.value?.signing_status || null)
const signers = computed(() => rocketLawyerInterview.value?.signers || [])
const isReadyToSend = computed(() => !signingStatus.value || signingStatus.value === 'in_preparation')
const isSigning = computed(() => ['sign_in_progress', 'partially_signed'].includes(signingStatus.value))
const isSigned = computed(() => signingStatus.value === 'signed')
// A lease that was sent, cancelled, and is now awaiting a resend (RAC-10448) —
// worth calling out so "Ready to Send" doesn't read as a brand-new lease.
const wasPulledBack = computed(() => Boolean(rocketLawyerInterview.value?.last_signature_cancelled_at))

const signingBadge = computed(() => {
    if (isSigned.value) { return { label: 'Fully Executed', class: 'bg-green-100 text-green-700' } }
    if (isSigning.value) { return { label: 'Out for Signature', class: 'bg-orange-100 text-orange-600' } }
    return { label: 'Ready to Send', class: 'bg-rent-risk-blue-100 text-rent-risk-blue-400' }
})

const signerStatusMeta = (status) => {
    if (status === 'signed') { return { label: 'Signed', class: 'bg-green-100 text-green-700' } }
    if (status === 'invited') { return { label: 'Awaiting Signature', class: 'bg-orange-100 text-orange-600' } }
    return { label: 'Invite Pending', class: 'bg-gray-100 text-gray-600' }
}

// The roster is fixed once the lease is generated (signature boxes seed from the
// interview's cycles), so changing who signs means starting over (RAC-10199).
const signerRosterNote = computed(() => {
    if (isSigned.value) { return 'This lease is fully executed — the signer list is final.' }
    if (isSigning.value) { return 'The signer list is locked while the lease is out for signature. To change it, cancel the signature request first.' }
    return 'Need to change who signs? Use Manage Signers to add or remove tenants before sending.'
})

const showWizard = ref(false)
const showRosterStep = ref(false)
const rosterTenants = ref([])
const wizardSessionKey = ref(0)
const forceWizardRestart = ref(false)

const howItWorksSteps = [
    { icon: faFilePen, title: 'Create Lease', description: 'Answer questions and build your lease' },
    { icon: faMagnifyingGlass, title: 'Review & Customize', description: 'Review, edit, and add additional terms' },
    { icon: faFileSignature, title: 'Send for E-Signature', description: 'Send to tenant(s) for e-signature' },
    { icon: faGavel, title: 'Fully Executed', description: 'Lease is signed and legally binding' },
]

// One key for every property: a landlord who closes the banner once has said
// they don't want it, not that they don't want it on this one address.
const MARKETING_BANNER_STORAGE_KEY = 'lease_marketing_banner_dismissed'

const showMarketingBanner = ref(
    typeof window !== 'undefined' && localStorage.getItem(MARKETING_BANNER_STORAGE_KEY) !== 'true'
)

const dismissMarketingBanner = () => {
    showMarketingBanner.value = false

    if (typeof window !== 'undefined') {
        localStorage.setItem(MARKETING_BANNER_STORAGE_KEY, 'true')
    }
}

const resumeProgressLabel = computed(() => {
    const interview = rocketLawyerInterview.value
    if (!interview) { return '' }
    const percent = Math.max(0, Math.min(100, Number(interview.current_page_index ?? 0)))
    return `${percent}% complete`
})

const downloadUrl = computed(() => {
    if (rocketLawyerInterview.value?.download_url) { return rocketLawyerInterview.value.download_url }
    if (leaseDocument.value?.url) { return leaseDocument.value.url }
    return null
})

const leaseAnswers = computed(() => rocketLawyerInterview.value?.answers || {})

const propertyStreetLine = computed(() =>
    [props.property?.address_1, props.property?.address_2].filter(Boolean).join(' ')
)

const leasePropertyAddress = computed(() => {
    return leaseAnswers.value?.property_address || propertyStreetLine.value || '—'
})

const leasePropertyCity = computed(() => {
    const a = leaseAnswers.value
    if (a?.property_city && a?.property_state) {
        return `${a.property_city}, ${a.property_state} ${a.property_zip || ''}`.trim()
    }
    const p = props.property
    if (p?.city && p?.state) {
        return `${p.city}, ${p.state} ${p.zip_code || ''}`.trim()
    }
    return ''
})

const leaseTenantName = computed(() => leaseAnswers.value?.tenant_name || '—')
const leaseTenantCount = computed(() => {
    const n = Number(rocketLawyerInterview.value?.tenant_count || 0)
    if (n > 0) { return `${n} tenant${n === 1 ? '' : 's'}` }
    // Legacy interviews predate the signer roster — fall back to the old heuristic.
    return leaseAnswers.value?.co_tenant_name ? '2 tenants' : '1 tenant'
})

const leaseTermDisplay = computed(() => {
    const a = leaseAnswers.value
    if (a?.lease_start_date && a?.lease_end_date) {
        const fmt = (d) => new Date(d).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
        return `${fmt(a.lease_start_date)} – ${fmt(a.lease_end_date)}`
    }
    return a?.lease_term || '—'
})

const leaseDuration = computed(() => leaseAnswers.value?.lease_duration || null)

const leaseMonthlyRent = computed(() => {
    const rent = leaseAnswers.value?.monthly_rent
    if (!rent) { return '—' }
    const num = parseFloat(String(rent).replace(/[^0-9.]/g, ''))
    if (isNaN(num)) { return String(rent) }
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
})

const formatDateTime = (iso) => {
    if (!iso) { return { date: '—', time: '' } }
    const d = new Date(iso)
    return {
        date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
    }
}

const leaseLastUpdatedDate = computed(() => formatDateTime(rocketLawyerInterview.value?.completed_at).date)
const leaseLastUpdatedTime = computed(() => formatDateTime(rocketLawyerInterview.value?.completed_at).time)

// "View Activity" in a document row's ⋯ menu (RAC-10476). The timeline is built
// server-side per row (LeaseDocumentListBuilder), oldest first.
const activityRow = ref(null)

const rowHasActivity = (row) => (row.activity?.length ?? 0) > 0

const openRowActivity = (row) => {
    activityRow.value = row
}

const closeRowActivity = () => {
    activityRow.value = null
}

// Resuming an in-progress interview skips the roster step — the roster is
// fixed at creation (signature boxes seed from the interview's cycles). A fresh
// lease or an explicit restart goes through the roster step first.
const openWizard = ({ restart = false } = {}) => {
    forceWizardRestart.value = restart
    showManageSigners.value = false

    const resuming = !restart
        && rocketLawyerInterview.value
        && rocketLawyerInterview.value.status === 'in_progress'

    if (resuming) {
        rosterTenants.value = []
        showRosterStep.value = false
        wizardSessionKey.value += 1
        showWizard.value = true
        return
    }

    showWizard.value = false
    showRosterStep.value = true
}

const handleRosterSubmit = (tenants) => {
    rosterTenants.value = tenants
    showRosterStep.value = false
    wizardSessionKey.value += 1
    showWizard.value = true
}

const cancelRoster = () => {
    showRosterStep.value = false
    forceWizardRestart.value = false
}

// "Create New Lease" is the single start-over action (RAC-10199). With no lease
// yet it just opens the wizard; when a lease already exists it replaces it
// (InterviewService restart path deletes the current interview), so we first
// explain what's lost and that editing the existing lease may be the better
// move.
const showNewLeaseModal = ref(false)

const createNewLease = () => {
    if (rocketLawyerInterview.value) {
        showNewLeaseModal.value = true
        return
    }
    openWizard()
}

const confirmNewLease = () => {
    showNewLeaseModal.value = false
    openWizard({ restart: true })
}

// "Create a Lease" now forks into Start New vs Upload Existing (RAC-10253).
// The chooser is the single entry point for both the top button and the empty
// states; picking a path closes the chooser and hands off to the existing flow.
const showCreateChooser = ref(false)

// One lease per property (RAC-10257). The server hands down the refusal it would
// give for each path, or null; the chooser greys out the matching tile and shows
// these words. Both handlers below re-check rather than trusting the disabled
// attribute alone — the tab can be open across a change made in another tab, and
// the endpoints refuse either way.
const leaseSlotUploadRefusal = computed(() => props.tabData?.lease_slot?.upload_refusal || null)
const leaseSlotNewLeaseRefusal = computed(() => props.tabData?.lease_slot?.new_lease_refusal || null)

const openCreateChooser = () => {
    if (isSharedViewOnly.value) { return }
    // RAC-10336: screening-only properties can now create a lease without upgrading to
    // managed — they take the same per-lease $49 gate (or Pro) as any non-Pro agent.
    // Buying a lease does not change the property's tier; it stays Screening Only. The
    // backend already permits this (RocketLawyerInterviewController gates on Protect
    // access + a paid LeaseCharge credit, never on tier), so no upgrade bounce here.
    // RAC-10292: lease creation is disabled until RocketLawyer is live — never
    // open the chooser, whose "Start New Lease" path would route to LawDepot.
    if (leaseComingSoon.value) { return }
    showCreateChooser.value = true
}

const chooseStartNewLease = () => {
    if (leaseSlotNewLeaseRefusal.value) { return }
    showCreateChooser.value = false
    if (rocketLawyerEnabled.value) {
        // RAC-10336: leases are gated per-property. A lease covered by the subscription
        // (a Managed property under RiskGuard/Pro) goes straight through, unlimited. A
        // prepaid, unconsumed $49 credit skips the gate too — the lease is paid for, so
        // re-prompting is wrong (the credit is consumed when start creates the interview).
        if (! leaseCoveredBySubscription.value && ! hasPrepaidLeaseCredit.value) {
            // Not covered → upgrade the account to Pro, or pay $49 for this lease. Nothing
            // is parked: paying goes on to createNewLease.
            openLeaseGateFor(null)
            return
        }
        createNewLease()
        return
    }
    if (lawdepotUrl.value) {
        window.open(lawdepotUrl.value, '_blank', 'noopener')
    }
}

// The lease card's own Replace button, which bypasses the chooser entirely.
const replaceLeaseDocument = () => {
    if (leaseSlotUploadRefusal.value) { return }
    openUploadModal()
}

const chooseUploadExisting = () => {
    if (leaseSlotUploadRefusal.value) { return }
    showCreateChooser.value = false
    openUploadModal()
}

// ===== Non-lease documents (RAC-10324) =====
// A launched document wizard session: {category, templateName, documentName,
// interviewId}. Null when no document wizard is open — the lease wizard keeps
// its own showWizard state untouched.
const documentWizard = ref(null)
const showExploreDocuments = ref(false)

const openDocsUpsell = () => {
    if (isSharedViewOnly.value) { return }
    showEsignUpsellModal.value = true
}

const chooseExploreDocuments = () => {
    showCreateChooser.value = false
    if (!isRiskGuardPro.value) {
        openDocsUpsell()
        return
    }
    showExploreDocuments.value = true
}

// Eviction notices are Pro-only, same as the rest of the document catalog. The
// interview is state-specific and prefilled from the property (RAC-10343).
const chooseEvictionNotice = () => {
    showCreateChooser.value = false
    if (!isRiskGuardPro.value) {
        openDocsUpsell()
        return
    }
    launchDocumentWizard({ category: 'eviction_notice', documentName: 'Eviction Notice' })
}

const handleExploreSelect = (name) => {
    showExploreDocuments.value = false
    launchDocumentWizard({ category: 'form', templateName: name, documentName: name })
}

// Sending a generated document for e-signature (RAC-10324). Its signature blocks
// come from RocketLawyer, so the step is opened by fetching them — the landlord
// fills in who each block belongs to, and cannot add or remove one.
const documentSignerStep = ref(null)
const sendingDocument = ref(false)
const documentSendError = ref(null)
const loadingSignersFor = ref(null)
const documentSignerNotice = ref(null)

const openDocumentSignerStep = async (row) => {
    if (loadingSignersFor.value !== null || isSharedViewOnly.value) {
        return
    }
    loadingSignersFor.value = row.action.interview_id
    documentSendError.value = null
    documentSignerNotice.value = null

    try {
        const { data } = await axios.get(`/properties/${props.property.id}/document-wizard/${row.action.interview_id}/signers`)

        if (!data.signers || data.signers.length === 0) {
            // Nothing to collect — don't take over the tab just to say so.
            documentSignerNotice.value = `${data.document_name} has no signature blocks, so it doesn't need to be signed.`
            return
        }

        documentSignerStep.value = {
            interviewId: row.action.interview_id,
            documentName: data.document_name,
            signers: data.signers,
        }
    } catch (error) {
        if (error.response?.status === 403 && error.response?.data?.upgrade_required) {
            openDocsUpsell()
            return
        }
        documentSignerNotice.value = error.response?.data?.message
            || 'Could not open the signing step. Please try again.'
    } finally {
        loadingSignersFor.value = null
    }
}

const handleSendDocument = async (signers) => {
    if (sendingDocument.value || documentSignerStep.value === null) {
        return
    }
    sendingDocument.value = true
    documentSendError.value = null

    try {
        await axios.post(
            `/properties/${props.property.id}/document-wizard/${documentSignerStep.value.interviewId}/send-for-signing`,
            { signers },
        )
        documentSignerStep.value = null
        router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
    } catch (error) {
        if (error.response?.status === 403 && error.response?.data?.upgrade_required) {
            documentSignerStep.value = null
            openDocsUpsell()
            return
        }
        documentSendError.value = error.response?.data?.message
            || error.response?.data?.errors?.['signers.0.email']?.[0]
            || 'Could not send this document for signature. Please try again.'
    } finally {
        sendingDocument.value = false
    }
}

const launchDocumentWizard = ({ category, templateName = null, documentName, interviewId = null }) => {
    documentWizard.value = { category, templateName, documentName, interviewId }
    wizardSessionKey.value += 1
}

// Row "Continue" — a lease draft resumes through the lease wizard; a document
// draft resumes its own interview by id.
const handleContinueRow = (row) => {
    if (row.action?.interview_id) {
        launchDocumentWizard({
            category: row.action.category,
            documentName: row.document_type,
            interviewId: row.action.interview_id,
        })
        return
    }
    openWizard()
}

const handleDocumentWizardCompleted = () => {
    documentWizard.value = null
    router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
}

// ===== Uploaded lease e-signature (RAC-10257) =====
// A lease the landlord uploaded themselves. Unlike a generated document there
// are no signature blocks to fetch — an uploaded PDF has no template — so the
// roster is authored on our form, then RocketLawyer's own editor is where the
// signature boxes get placed.
const uploadedLeaseSignerStep = ref(null)
const preparingUploadedLease = ref(false)
const uploadedLeaseError = ref(null)
const uploadedLeaseNotice = ref(null)
const uploadedLeaseAdoptedNotice = ref(null)

const page = usePage()

// The lowest Pro tier as a monthly figure; Pro is billed yearly.
const proStartingAtCopy = computed(() => {
    const lowestTier = page.props.subscription_prices?.pro_tiers?.[0]

    return lowestTier
        ? `Starting at ${formatMonthly(lowestTier.monthly_equivalent)}/mo, billed annually.`
        : 'Billed annually.'
})
const agentName = computed(() => page.props?.auth?.user?.name || '')
const agentEmail = computed(() => page.props?.auth?.user?.email || '')

// The primary button: set the lease up, or send it. Placing boxes is its own
// secondary button beside this one — it is not a step that ends, since the
// landlord can reopen RocketLawyer's editor at any point before sending.
const uploadedPrimaryAction = (row) => {
    if (row.action?.can_send) {
        return { step: 'send', label: 'Send for Signature' }
    }

    if (row.action?.can_prepare) {
        return { step: 'prepare', label: 'Sign Lease' }
    }

    return null
}

// Which row's button was pressed. preparingUploadedLease is a single flag shared
// by every uploaded row (one in-flight action at a time, on purpose), so it
// cannot say WHICH row is working — and labelling them all "Sending…" would be a
// lie about the rows nobody touched.
const uploadedActionFor = ref(null)

const handleUploadedPrimaryAction = (row) => {
    if (isSharedViewOnly.value || preparingUploadedLease.value) {
        return
    }

    uploadedLeaseError.value = null
    uploadedLeaseNotice.value = null
    uploadedLeaseAdoptedNotice.value = null

    const action = uploadedPrimaryAction(row)

    if (action?.step === 'send') {
        sendUploadedLease(row.action.interview_id)

        return
    }

    // Setting the lease up is where the binder is created and the fee falls due,
    // so it is the only step gated on the property's tier. Place and Send act on
    // a lease that is already set up — and therefore already covered or paid —
    // and re-gating them would strand it.
    if (! esignCoveredForProperty.value) {
        openLeaseGateFor(() => openUploadedLeaseSignerStep(row))

        return
    }

    openUploadedLeaseSignerStep(row)
}

const openUploadedLeaseSignerStep = (row) => {
    uploadedLeaseSignerStep.value = {
        documentId: row.action.document_id,
        documentType: row.document_type,
    }
}

// A full navigation, not an Inertia visit: the placement screen is a standalone
// blade hosting RocketLawyer's iframe.
const goToPlacement = (row) => {
    if (row.action?.placement_url) {
        window.location.href = row.action.placement_url
    }
}

// Creates the binder, uploads the PDF into it, then hands the landlord straight
// to RocketLawyer's editor. A full navigation rather than an Inertia visit: the
// placement screen is a standalone blade hosting RL's iframe, not an Inertia page.
const handlePrepareUploadedLease = async (signers) => {
    if (preparingUploadedLease.value || uploadedLeaseSignerStep.value === null) {
        return
    }

    preparingUploadedLease.value = true
    uploadedLeaseError.value = null

    try {
        const { data } = await axios.post(
            `/properties/${props.property.id}/documents/${uploadedLeaseSignerStep.value.documentId}/prepare-signing`,
            { signers },
        )

        window.location.href = data.placement_url
    } catch (error) {
        if (error.response?.status === 403 && error.response?.data?.upgrade_required) {
            uploadedLeaseSignerStep.value = null
            openEsignUpsell()

            return
        }

        if (error.response?.status === 402 && error.response?.data?.managed_upgrade_required) {
            const step = uploadedLeaseSignerStep.value
            uploadedLeaseSignerStep.value = null
            openLeaseGateFor(() => { uploadedLeaseSignerStep.value = step })

            return
        }

        uploadedLeaseError.value = error.response?.data?.message
            || 'We could not set this lease up for signing. Please try again.'
    } finally {
        preparingUploadedLease.value = false
    }
}

const sendUploadedLease = async (interviewId) => {
    if (preparingUploadedLease.value) {
        return
    }

    preparingUploadedLease.value = true
    uploadedActionFor.value = interviewId
    uploadedLeaseNotice.value = null

    try {
        const { data } = await axios.post(`/properties/${props.property.id}/uploaded-lease/${interviewId}/send-for-signing`)

        // The lease was already out for signature at RocketLawyer, so Send
        // recorded it rather than inviting anyone. Nobody got a RentRisk email,
        // and saying nothing would leave the landlord watching for one.
        if (data?.adopted) {
            uploadedLeaseAdoptedNotice.value = 'This lease was already sent from inside RocketLawyer, so RocketLawyer emailed the signers rather than RentRisk. We\u2019ve recorded it \u2014 nobody was emailed twice, and you can follow the signatures here.'
        }

        router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
    } catch (error) {
        if (error.response?.status === 403 && error.response?.data?.upgrade_required) {
            openEsignUpsell()

            return
        }

        uploadedLeaseNotice.value = error.response?.data?.message
            || 'We could not send this lease for signature. Please try again.'
    } finally {
        preparingUploadedLease.value = false
        uploadedActionFor.value = null
    }
}

// Its own menu-action list: an uploaded row always offers Delete from the menu
// once it has a signing lifecycle, and never offers Edit Document.
const uploadedRowMenuActions = (row) => {
    if (isSharedViewOnly.value) {
        return []
    }

    const actions = []
    if (row.action?.can_cancel && isRiskGuardPro.value) {
        actions.push('cancel')
    }
    if (row.action?.can_refresh) {
        actions.push('refresh')
    }

    return actions
}

const openCancelUploadedLeaseSignature = (row) => {
    if (isSharedViewOnly.value || !row.action?.interview_id) {
        return
    }

    documentCancelError.value = null
    uploadedLeaseCancelInterviewId.value = row.action.interview_id
    documentCancelStep.value = {
        interviewId: row.action.interview_id,
        documentName: row.document_type,
        signers: (row.tenants || []).map((party) => ({
            name: party.name,
            status: party.status,
        })),
    }
}

// Reuses the document cancel step's UI and state; only the endpoint differs, so
// this flag records which one the open step belongs to.
const uploadedLeaseCancelInterviewId = ref(null)

const refreshUploadedLeaseStatus = async (row) => {
    const interviewId = row?.action?.interview_id
    if (isSharedViewOnly.value || refreshingDocumentId.value || !interviewId) { return }
    refreshingDocumentId.value = interviewId
    documentRefreshMessage.value = null
    try {
        const { data } = await axios.post(
            `/properties/${props.property.id}/uploaded-lease/${interviewId}/refresh-signing`
        )
        if (data?.refreshed || data?.out_for_signature === false) {
            router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
        } else if (data?.retry_after_seconds > 0) {
            startDocumentRefreshCountdown(interviewId, data.retry_after_seconds)
        } else {
            documentRefreshMessage.value = 'Could not refresh signing status right now — showing the last known status.'
        }
    } catch (error) {
        documentRefreshMessage.value = error.response?.data?.message || 'Could not refresh signing status. Please try again.'
    } finally {
        refreshingDocumentId.value = null
    }
}

// Which secondary actions this row has — drives whether the ⋯ menu renders at
// all, so a row with nothing behind it doesn't get an empty trigger.
const rowMenuActions = (row) => {
    if (isSharedViewOnly.value) {
        return []
    }

    const actions = []
    if (row.action?.can_edit) {
        actions.push('edit')
    }
    if (row.action?.can_cancel && isRiskGuardPro.value) {
        actions.push('cancel')
    }
    if (row.action?.can_refresh) {
        actions.push('refresh')
    }

    return actions
}

// Re-open a generated document so an answer can be changed and the document
// regenerated. Same entry point the draft "Continue" button uses — the wizard
// resumes the interview at the page it was left on, and its Back button walks
// to earlier answers.
const handleEditDocument = (row) => {
    if (!row.action?.interview_id) {
        return
    }

    launchDocumentWizard({
        category: row.action.category ?? row.category,
        documentName: row.document_type,
        interviewId: row.action.interview_id,
    })
}

// Cancel a document out for signature (RAC-10324). RocketLawyer refuses to edit
// a signer party while the binder is out for signature, so this is the only way
// to correct a typo'd email — it pulls the binder back to in_preparation, which
// re-opens both Edit and Send for Signature.
const documentCancelStep = ref(null)
const cancellingDocument = ref(false)
const documentCancelError = ref(null)

const openCancelDocumentSignature = (row) => {
    if (isSharedViewOnly.value || !row.action?.interview_id) {
        return
    }

    documentCancelError.value = null
    documentCancelStep.value = {
        interviewId: row.action.interview_id,
        documentName: row.document_type,
        // The row already carries every signer with their own status, which is
        // all the step needs to name who it is about to void a signature for.
        signers: (row.tenants || []).map((party) => ({
            name: party.name,
            status: party.status,
        })),
    }
}

const handleCancelDocumentSignature = async (payload) => {
    if (cancellingDocument.value || !documentCancelStep.value) {
        return
    }

    cancellingDocument.value = true
    documentCancelError.value = null

    // Same step, same payload — an uploaded lease just cancels through its own
    // controller (RAC-10257), which additionally clears the confirmed placement
    // so the boxes can be re-checked before the next send.
    const isUploadedLease = uploadedLeaseCancelInterviewId.value === documentCancelStep.value.interviewId
    const cancelPath = isUploadedLease ? 'uploaded-lease' : 'document-wizard'

    try {
        await axios.post(
            `/properties/${props.property.id}/${cancelPath}/${documentCancelStep.value.interviewId}/cancel-signature`,
            payload
        )
        documentCancelStep.value = null
        uploadedLeaseCancelInterviewId.value = null
        router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
    } catch (error) {
        documentCancelError.value = error.response?.data?.message
            || 'We could not cancel this signature request. Please try again.'
    } finally {
        cancellingDocument.value = false
    }
}

// Backend Pro gate rejected the start (403 + upgrade_required) — e.g. the
// subscription lapsed after the page loaded. Leave focus mode and show the
// same upsell the tile-level gate uses.
const handleDocumentUpgradeRequired = () => {
    documentWizard.value = null
    openDocsUpsell()
}

// Edit the current lease in place: resume the existing interview straight into
// the wizard (no roster step, restart=false), change answers, and re-complete to
// regenerate. RL confirmed POST /completions keeps the binder parties + document
// in sync, so editing through the interview is the supported path (RAC-10192).
// Only offered before the lease is sent — it's locked once out for signature.
const editLease = () => {
    forceWizardRestart.value = false
    rosterTenants.value = []
    showRosterStep.value = false
    showManageSigners.value = false
    wizardSessionKey.value += 1
    showWizard.value = true
}

// Manage Signers: add/remove/edit who signs on a generated-but-unsent lease
// (RAC-10199 Phase 2). Reuses the roster editor, prefilled from the current
// signers, and PUTs to the signers endpoint — which re-seeds the interview's
// tenant cycles, regenerates the binder in sync, and reconciles the signer rows.
const showManageSigners = ref(false)
const savingSigners = ref(false)
const manageSignersError = ref(null)

const manageSignersDefaults = computed(() => rocketLawyerInterview.value?.signer_roster || [])

const openManageSigners = () => {
    showWizard.value = false
    showRosterStep.value = false
    manageSignersError.value = null
    showManageSigners.value = true
}

const handleManageSignersSubmit = async (tenants) => {
    if (isSharedViewOnly.value || savingSigners.value) { return }
    savingSigners.value = true
    manageSignersError.value = null
    try {
        await axios.put(`/properties/${props.property.id}/lease-wizard/signers`, { tenants })
        showManageSigners.value = false
        router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
    } catch (error) {
        manageSignersError.value = error.response?.data?.message
            || error.response?.data?.errors?.['tenants.0.email']?.[0]
            || 'Could not update the signers. Please try again.'
    } finally {
        savingSigners.value = false
    }
}

// Cancel signature request (RAC-10448): pulls the lease back to in_preparation
// so it can be corrected and resent. RocketLawyer will not let a signer party be
// edited while the binder is out for signature, so this is the only way to fix a
// typo'd tenant email. Signatures already collected are voided — the modal warns
// by name before this runs.
const showCancelSignature = ref(false)
const cancellingSignature = ref(false)
const cancelSignatureError = ref(null)

const openCancelSignature = () => {
    showWizard.value = false
    showRosterStep.value = false
    showManageSigners.value = false
    cancelSignatureError.value = null
    showCancelSignature.value = true
}

const handleCancelSignature = async (payload) => {
    if (isSharedViewOnly.value || cancellingSignature.value) { return }
    cancellingSignature.value = true
    cancelSignatureError.value = null
    try {
        await axios.post(`/properties/${props.property.id}/lease-wizard/cancel-signature`, payload)
        showCancelSignature.value = false
        router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
    } catch (error) {
        cancelSignatureError.value = error.response?.data?.message
            || error.response?.data?.errors?.reason?.[0]
            || error.response?.data?.errors?.reason_note?.[0]
            || 'Could not cancel the signature request. Please try again.'
    } finally {
        cancellingSignature.value = false
    }
}

// What replacing the current lease costs, scaled to how far it has gone.
const newLeaseConsequence = computed(() => {
    if (isSigned.value) {
        return 'This lease is fully executed — its signing record and each signer\'s status will be removed (the executed PDF stays in your documents).'
    }
    if (isSigning.value) {
        return 'This lease is already out for signature — it will be discarded and you\'ll lose the current signing progress.'
    }
    if (rocketLawyerInterview.value?.status === 'completed') {
        return 'The lease you\'ve generated will be discarded and the signer roster cleared.'
    }
    return 'Your in-progress answers will be discarded.'
})

const handleWizardCompleted = () => {
    showWizard.value = false
    showRosterStep.value = false
    forceWizardRestart.value = false
}

// Focus mode: the lease builder (roster step → wizard, or manage signers) takes
// over the whole tab so the user isn't distracted by the marketing/help chrome.
// Progress is saved server-side on every step, so exiting just returns to the
// overview — the in-progress lease can be resumed from the Current Lease card.
const wizardFocusMode = computed(
    () => rocketLawyerEnabled.value
        && (showWizard.value || showRosterStep.value || showManageSigners.value || showCancelSignature.value
            || documentWizard.value !== null || documentSignerStep.value !== null
            || documentCancelStep.value !== null || uploadedLeaseSignerStep.value !== null)
        && !isSharedViewOnly.value
)

const exitWizardFocus = () => {
    showWizard.value = false
    showRosterStep.value = false
    showManageSigners.value = false
    showCancelSignature.value = false
    forceWizardRestart.value = false
    documentWizard.value = null
    documentSignerStep.value = null
    documentCancelStep.value = null
    uploadedLeaseSignerStep.value = null
    uploadedLeaseCancelInterviewId.value = null
}

// Send the generated lease for signature (RAC-10172 → RAC-10196 endpoint).
// On success, refresh tabData so the card flips to the signing state and the
// status poll below picks up. Each signer is emailed a signing link (RAC-10171).
const sending = ref(false)
const sendError = ref(null)

const sendForSignature = async () => {
    if (isSharedViewOnly.value || sending.value) { return }
    sending.value = true
    sendError.value = null
    try {
        await axios.post(`/properties/${props.property.id}/lease-wizard/send-for-signing`)
        router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
    } catch (error) {
        sendError.value = error.response?.data?.message || 'Could not send the lease for signature. Please try again.'
    } finally {
        sending.value = false
    }
}

// RAC-10361: signing status refreshes off the live binder when this tab loads
// (server-side, in PropertyController@show) and via this manual "Refresh" button,
// with an hourly reconcile cron as the safety net — replacing the 15s client poll
// (which, even capped, produced thousands of partial reloads per open tab). The
// on-load refresh and this button share one per-lease throttle on the backend
// and the endpoint is also rate-limited per user, so neither can hammer
// RocketLawyer; when throttled the backend returns the seconds until the next
// allowed refresh instead of calling RL.
const refreshing = ref(false)
const refreshError = ref(null)

// A refused refresh used to say "Just refreshed", which was not what happened —
// the last fetch may have been most of a minute ago, and the message left the
// landlord to guess when to try again. Count the window down instead and clear
// it when it expires, so the button becomes usable again on its own.
const refreshRetryIn = ref(0)
let refreshCountdownTimer = null

const runCountdown = (seconds, tick, done) => {
    tick(seconds)
    const timer = setInterval(() => {
        seconds -= 1
        tick(seconds)
        if (seconds <= 0) {
            clearInterval(timer)
            done()
        }
    }, 1000)
    return timer
}

const startRefreshCountdown = (seconds) => {
    if (refreshCountdownTimer) { clearInterval(refreshCountdownTimer) }
    refreshCountdownTimer = runCountdown(
        Math.max(1, Number(seconds) || 1),
        (left) => {
            refreshRetryIn.value = left
            refreshError.value = left > 0 ? `Checked a moment ago — checking again in ${left}s.` : null
        },
        () => { refreshRetryIn.value = 0; refreshError.value = null; refreshCountdownTimer = null }
    )
}

// Keyed by interview id, not a single value: the throttle it mirrors is
// per-interview, and a property can hold several documents out for signature at
// once. A shared counter would disable every row's Refresh because one of them
// was throttled, even though the others have never been fetched.
const documentRefreshRetryIn = ref({})
const documentCountdownTimers = {}

const startDocumentRefreshCountdown = (interviewId, seconds) => {
    if (documentCountdownTimers[interviewId]) { clearInterval(documentCountdownTimers[interviewId]) }
    documentCountdownTimers[interviewId] = runCountdown(
        Math.max(1, Number(seconds) || 1),
        (left) => {
            documentRefreshRetryIn.value = { ...documentRefreshRetryIn.value, [interviewId]: left }
            documentRefreshMessage.value = left > 0 ? `Checked a moment ago — checking again in ${left}s.` : null
        },
        () => {
            const { [interviewId]: _dropped, ...rest } = documentRefreshRetryIn.value
            documentRefreshRetryIn.value = rest
            documentRefreshMessage.value = null
            delete documentCountdownTimers[interviewId]
        }
    )
}

onUnmounted(() => {
    if (refreshCountdownTimer) { clearInterval(refreshCountdownTimer) }
    Object.values(documentCountdownTimers).forEach((timer) => clearInterval(timer))
})

const signingLastRefreshed = computed(() => {
    const iso = rocketLawyerInterview.value?.signing_last_refreshed_at
    if (!iso) { return null }
    const { date, time } = formatDateTime(iso)
    return `${date} at ${time}`
})

const refreshSigningStatus = async () => {
    if (isSharedViewOnly.value || refreshing.value) { return }
    refreshing.value = true
    refreshError.value = null
    try {
        const { data } = await axios.post(`/properties/${props.property.id}/lease-wizard/refresh-signing`)
        if (data?.refreshed) {
            router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
        } else if (data?.out_for_signature === false) {
            // Nothing to fetch because the lease already moved on (the cron, or
            // another tab, completed it). That is stale state, not a failure —
            // reload so the badge stops saying "Out for Signature".
            router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
        } else if (data?.retry_after_seconds > 0) {
            startRefreshCountdown(data.retry_after_seconds)
        } else {
            refreshError.value = 'Could not refresh signing status right now — showing the last known status.'
        }
    } catch (error) {
        refreshError.value = error.response?.data?.message || 'Could not refresh signing status. Please try again.'
    } finally {
        refreshing.value = false
    }
}

// RAC-10361: the on-load refresh above covers the lease singleton only — one
// binder GET per page view is the ceiling this feature exists to hold, and a
// property can carry many documents. So an out-for-signature document gets the
// same manual Refresh instead, keeping the affordance #1532 shipped. Keyed on
// the interview because the endpoint is (documents accumulate per property).
const refreshingDocumentId = ref(null)
const documentRefreshMessage = ref(null)

const refreshDocumentStatus = async (row) => {
    const interviewId = row?.action?.interview_id
    if (isSharedViewOnly.value || refreshingDocumentId.value || !interviewId) { return }
    refreshingDocumentId.value = interviewId
    documentRefreshMessage.value = null
    try {
        const { data } = await axios.post(
            `/properties/${props.property.id}/document-wizard/${interviewId}/refresh-signing`
        )
        if (data?.refreshed) {
            router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
        } else if (data?.out_for_signature === false) {
            router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
        } else if (data?.retry_after_seconds > 0) {
            startDocumentRefreshCountdown(interviewId, data.retry_after_seconds)
        } else {
            documentRefreshMessage.value = 'Could not refresh signing status right now — showing the last known status.'
        }
    } catch (error) {
        documentRefreshMessage.value = error.response?.data?.message || 'Could not refresh signing status. Please try again.'
    } finally {
        refreshingDocumentId.value = null
    }
}

const showUploadModal = ref(false)
const showDeleteModal = ref(false)
const selectedFile = ref(null)
const uploading = ref(false)
const deleting = ref(false)
const uploadError = ref(null)
const uploadNotice = ref(null)
const deleteError = ref(null)
const fileInput = ref(null)

const handleFileChange = (event) => {
    selectedFile.value = event.target.files[0] || null
    uploadError.value = null
}

const openUploadModal = () => {
    // Clear last time's confirmation: a stale "uploaded" line sitting above a
    // modal the landlord is about to use again reads as this upload's result.
    uploadNotice.value = null
    showUploadModal.value = true
}

const closeUploadModal = () => {
    showUploadModal.value = false
    selectedFile.value = null
    uploadError.value = null
    if (fileInput.value) { fileInput.value.value = '' }
}

const uploadFile = async () => {
    if (isSharedViewOnly.value || !selectedFile.value) { return }
    uploading.value = true
    uploadError.value = null
    const formData = new FormData()
    formData.append('file', selectedFile.value)
    formData.append('type', 'lease_agreement')
    try {
        const { data } = await axios.post(`/properties/${props.property.id}/documents/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
        closeUploadModal()
        uploadNotice.value = data?.message || 'Document uploaded successfully.'
        router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
    } catch (error) {
        uploadError.value = error.response?.data?.errors?.file?.[0] || error.response?.data?.message || 'An error occurred while uploading. Please try again.'
    } finally {
        uploading.value = false
    }
}

// The delete modal targets one document id — the legacy singleton card (via
// leaseDocument) or a specific "Your Documents" uploaded row.
const documentPendingDelete = ref(null)

// An uploaded lease that has been fully signed is the executed contract, and its
// PropertyDocument row is the only handle the app has on it — there is no second
// copy and no route back once it goes. Worth its own warning rather than the
// generic "this cannot be undone" (RAC-10257).
const deletingAnExecutedLease = ref(false)

const confirmDelete = () => {
    if (isSharedViewOnly.value || !leaseDocument.value) { return }
    deleteError.value = null
    documentPendingDelete.value = leaseDocument.value.id
    deletingAnExecutedLease.value = false
    showDeleteModal.value = true
}

const confirmDeleteUploadedDocument = (documentId, row = null) => {
    if (isSharedViewOnly.value) { return }
    deleteError.value = null
    documentPendingDelete.value = documentId
    deletingAnExecutedLease.value = row?.action?.signing_status === 'signed'
    showDeleteModal.value = true
}

const closeDeleteModal = () => {
    showDeleteModal.value = false
    documentPendingDelete.value = null
    deletingAnExecutedLease.value = false
    deleteError.value = null
}

const deleteDocument = async () => {
    if (isSharedViewOnly.value || !documentPendingDelete.value) { return }
    deleting.value = true
    deleteError.value = null
    try {
        // The endpoint refuses to destroy the executed copy of an uploaded lease
        // unless the caller says it means to (RAC-10257). The modal has already
        // spelled out what is lost, so confirming here IS that acknowledgement.
        await axios.delete(`/properties/${props.property.id}/documents/${documentPendingDelete.value}`, {
            data: deletingAnExecutedLease.value ? { acknowledge_executed: true } : {},
        })
        closeDeleteModal()
        router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
    } catch (error) {
        // Deliberately leaves the modal open and the pending id in place: the
        // delete may have been refused because the lease is out for signature
        // (RAC-10257), and the landlord needs to read why. Previously this
        // logged to the console and left a modal that looked hung.
        //
        // One refusal is not an error to read but a question to answer: the
        // endpoint will not destroy the executed copy of an uploaded lease
        // until the caller says it means to. The legacy singleton card has no
        // row to tell it that in advance — its document is simply the newest
        // lease_agreement on the property, executed or not — so the refusal
        // itself escalates the modal to the copy naming what is lost, and the
        // next confirm carries the acknowledgement.
        if (error.response?.data?.requires_acknowledgement === 'executed_lease') {
            deletingAnExecutedLease.value = true
        } else {
            deleteError.value = error.response?.data?.message
                || 'We could not delete this document. Please try again.'
        }
    } finally {
        deleting.value = false
    }
}

// 86bcd9hg3: the Pro price moved before the charge (nothing was charged); Show.vue
// hands the retry back here so the same pop-up (and its $49 option) shows again.
const handleTierChanged = (message = null) => {
    tierChangedNotice.value = message
    if (lastProUpsell.value === 'leaseGate') {
        showLeaseGateModal.value = true
    } else {
        showEsignUpsellModal.value = true
    }
}

defineExpose({ handleTierChanged })
</script>
